import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { z } from "zod";

import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import { api } from "~/shared/services/api/fetch-client.ts";

import type { CreateFTLQuoteCommand } from "../create-quote/create-ftl-quote";
import { mapCreateFtlQuoteErrorToUserMessage } from "./use-create-ftl-quote-error-mapper.ts";

const createFtlQuoteErrorSchema = z.array(
	z.object({
		code: z.string(),
		message: z.string(),
	}),
);

// Zod schema for created quote response validation
const createdQuoteSchema = z.object({
	id: z.number(),
	rate: z.number().nullable(),
	rateAttempted: z.boolean(),
	transitDays: z.number().nullable(),
});

export type CreatedQuote = z.infer<typeof createdQuoteSchema>;

async function createFtlQuote(
	request: CreateFTLQuoteCommand,
): Promise<CreatedQuote> {
	const response = await api.post("ftl-quote", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request),
		hooks: {
			beforeError: [
				async (error) => {
					const { response } = error;
					if (response) {
						const contentType = response.headers.get("content-type") ?? "";
						if (!contentType.toLowerCase().includes("application/json")) {
							return error;
						}

						try {
							const responseBody: unknown = await response.clone().json();
							const parsedError =
								createFtlQuoteErrorSchema.safeParse(responseBody);
							if (parsedError.success) {
								const messages = parsedError.data
									.map((e) => e.message)
									.join(" ");
								error.message = messages;
							}
						} catch {
							// Keep the original HTTP error so downstream status-based mapping still works.
							return error;
						}
					}
					return error;
				},
			],
		},
	});
	const data = await response.json();

	// Validate response with Zod schema
	return createdQuoteSchema.parse(data);
}

type UseCreateFtlQuoteOptions = {
	onSuccess?: (data: CreatedQuote, variables: CreateFTLQuoteCommand) => void;
	onSettled?: () => void;
};

export function useCreateFtlQuote(options?: UseCreateFtlQuoteOptions) {
	const { showError } = useNotifications();
	const consecutiveFailureCountRef = useRef(0);

	const { data, error, isPending, mutate } = useMutation({
		mutationFn: createFtlQuote,
		onSuccess: (data, variables) => {
			consecutiveFailureCountRef.current = 0;

			// Handle successful creation
			// Call custom onSuccess callback if provided
			if (options?.onSuccess) {
				options.onSuccess(data, variables);
			}
		},
		onError: async (error) => {
			consecutiveFailureCountRef.current += 1;

			showError(
				await mapCreateFtlQuoteErrorToUserMessage(
					error,
					consecutiveFailureCountRef.current,
				),
			);
			console.log("Error occurred while creating FTL quote:", error);
		},
		onSettled: () => {
			options?.onSettled?.();
		},
	});

	return {
		data,
		error,
		isPending,
		createFtlQuote: mutate,
	};
}
